/**
 * Wiring the queue into the app — docs/sync-state-machine.md §3, §12.
 *
 * Two things the rest of the app gets from here:
 *
 *   `record`  — enqueue first, then try to send. Rule L-1: the op is durable
 *               before anything is confirmed, so a phone that dies between the
 *               tap and the network still has the result.
 *   `status`  — what the indicator shows. Rule U-1: offline is presented as
 *               normal, never as failure. The club plays in a sports hall.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { SyncQueue, type OpType, type QueueCounts, type SyncOp } from "./queue.js";
import { Sender, type FlushOutcome } from "./sender.js";

export type IndicatorState =
  | "synced"
  | "pending"
  | "syncing"
  | "conflict"
  | "failed"
  | "offline";

export interface SyncStatus {
  state: IndicatorState;
  counts: QueueCounts;
  /** ST-2 — a queue this deep or this old warrants a standing warning. */
  warning: string | null;
  /** O-4 — the device clock is far enough out to degrade duplicate detection. */
  clockSkewSeconds: number | null;
}

const IDLE: QueueCounts = {
  pending: 0,
  inflight: 0,
  conflict: 0,
  failed: 0,
  oldestPendingAgeMs: null,
};

export interface UseSync {
  status: SyncStatus;
  record: (opType: OpType, payload: Record<string, unknown>, entityId?: string) => Promise<SyncOp>;
  flush: () => Promise<FlushOutcome | undefined>;
  pendingOps: SyncOp[];
  exportUnsent: () => Promise<string>;
}

export function useSync(options: { onSynced?: () => void } = {}): UseSync {
  const queueRef = useRef<SyncQueue | null>(null);
  const senderRef = useRef<Sender | null>(null);
  if (!queueRef.current) {
    queueRef.current = new SyncQueue();
    senderRef.current = new Sender(queueRef.current);
  }

  const [counts, setCounts] = useState<QueueCounts>(IDLE);
  const [pendingOps, setPendingOps] = useState<SyncOp[]>([]);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [clockSkewSeconds, setClockSkew] = useState<number | null>(null);
  const onSynced = options.onSynced;

  const refreshCounts = useCallback(async () => {
    const queue = queueRef.current!;
    setCounts(await queue.counts());
    // Rule L-2 — unsent work stays visible, so standings and the game list
    // include what this device knows even before the server hears about it.
    setPendingOps(await queue.byState("pending"));
  }, []);

  const flush = useCallback(async (): Promise<FlushOutcome | undefined> => {
    if (syncing) return undefined;
    setSyncing(true);
    try {
      const outcome = await senderRef.current!.flush();
      if (outcome.clockSkewSeconds) setClockSkew(outcome.clockSkewSeconds);
      await refreshCounts();
      if (outcome.applied > 0) onSynced?.();
      return outcome;
    } finally {
      setSyncing(false);
    }
  }, [refreshCounts, syncing, onSynced]);

  const record = useCallback(
    async (opType: OpType, payload: Record<string, unknown>, entityId?: string) => {
      // Durable first. Everything else can fail without losing the result.
      const op = await queueRef.current!.enqueue({ opType, payload, entityId });
      await refreshCounts();
      void flush();
      return op;
    },
    [flush, refreshCounts],
  );

  useEffect(() => {
    void refreshCounts();
  }, [refreshCounts]);

  // §10 — retry immediately on regained connectivity, and treat losing it as
  // an ordinary state change rather than an error.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void senderRef.current!.onReconnect().then(refreshCounts);
    };
    const goOffline = () => setOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [refreshCounts]);

  // A slow heartbeat picks up ops whose backoff has expired. Deliberately not
  // aggressive: the queue is not urgent, and a phone at a court is on battery.
  useEffect(() => {
    const timer = setInterval(() => void flush(), 20_000);
    return () => clearInterval(timer);
  }, [flush]);

  return {
    status: {
      state: indicatorState({ counts, online, syncing }),
      counts,
      warning: warningFor(counts),
      clockSkewSeconds,
    },
    record,
    flush,
    pendingOps,
    exportUnsent: () => queueRef.current!.exportUnsent(),
  };
}

/**
 * §12 — the order matters. A conflict or a failure needs a human and outranks
 * everything; being offline with a queue is normal and ranks below both.
 */
function indicatorState(input: {
  counts: QueueCounts;
  online: boolean;
  syncing: boolean;
}): IndicatorState {
  const { counts, online, syncing } = input;
  if (counts.conflict > 0) return "conflict";
  if (counts.failed > 0) return "failed";
  if (!online) return "offline";
  if (syncing || counts.inflight > 0) return "syncing";
  if (counts.pending > 0) return "pending";
  return "synced";
}

/** ST-2, ST-4 — said plainly, before it becomes a problem. */
function warningFor(counts: QueueCounts): string | null {
  const unsent = counts.pending + counts.inflight;
  if (unsent >= 50) {
    return `${unsent} results are waiting to sync. Find signal when you can, or export them.`;
  }
  if (counts.oldestPendingAgeMs !== null && counts.oldestPendingAgeMs > 24 * 60 * 60 * 1000) {
    return "Some results have been waiting over a day. Find signal, or export them.";
  }
  if (counts.failed > 0) {
    return `${counts.failed} ${counts.failed === 1 ? "result needs" : "results need"} correcting before they can sync.`;
  }
  return null;
}
